import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductTile from '../components/products/ProductTile';
import { products as productMeta, productCategories } from '../data/productRegistry';
import { useLocalizedProducts, useLocalizedCategories, useLocalizedDeployments } from '../hooks/useLocalizedProducts';

const ProductsPage = () => {
  const { t } = useTranslation('editor');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const localizedProducts = useLocalizedProducts();
  const localizedCategories = useLocalizedCategories();
  const localizedDeployments = useLocalizedDeployments();

  const filtered = activeCategory === 'all'
    ? productMeta
    : productMeta.filter((p) => p.category === activeCategory);

  const filters = [
    { id: 'all', label: t('productsPage.all'), icon: LayoutGrid, count: productMeta.length },
    ...productCategories.map((cat) => ({
      id: cat.id,
      label: localizedCategories.find((lc) => lc.id === cat.id)?.label ?? cat.label,
      icon: cat.icon,
      count: productMeta.filter((p) => p.category === cat.id).length,
    })),
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="fixed top-0 left-0 right-0 z-50">
        <Navbar />
      </div>

      <section className="relative pt-44 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b" />
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-gradient-to-r from-blue-600/25 via-cyan-500/20 to-emerald-500/10 blur-[120px] animate-aurora" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('productPage.allProducts')}
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
              <span className="text-cyan-200 text-sm font-semibold tracking-wide">{t('productsPage.badge')}</span>
            </div>
            <h1 className="font-display text-5xl lg:text-7xl font-bold text-white mb-6 leading-[1.05]">
              <span className="text-gradient animate-shimmer">{t('productsPage.title')}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-400 max-w-2xl mx-auto">{t('productsPage.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <section className="sticky top-[80px] md:top-[136px] z-30 py-4 bg-slate-950/80 backdrop-blur-xl border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto no-scrollbar justify-start md:justify-center">
            {filters.map((f) => {
              const active = activeCategory === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setActiveCategory(f.id)}
                  className={`relative flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    active ? 'text-slate-950' : 'text-slate-300 hover:text-white border border-white/10 hover:border-white/25'
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="product-filter"
                      className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-300 to-emerald-300"
                      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    />
                  )}
                  <f.icon className="relative h-4 w-4" />
                  <span className="relative">{f.label}</span>
                  <span className={`relative text-[11px] font-bold px-1.5 rounded-full ${active ? 'bg-slate-950/15' : 'bg-white/10 text-slate-400'}`}>
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((product, i) => {
                const loc = localizedProducts.find((lp) => lp.slug === product.slug);
                const deploymentLabels = product.deployments
                  .slice(0, 3)
                  .map((d) => localizedDeployments.find((ld) => ld.id === d)?.name ?? d);
                return (
                  <motion.div
                    key={product.slug}
                    layout
                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.3, delay: Math.min(i, 8) * 0.04 }}
                  >
                    <ProductTile
                      product={product}
                      name={loc?.name ?? product.name}
                      tagline={loc?.tagline ?? product.tagline}
                      description={loc?.description ?? product.description}
                      cta={t('productsPage.explore')}
                      deploymentLabels={deploymentLabels}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
